import { DownloadButton, TextSize, Typography } from '@shared/index';
import { Table, TableBody, TableTd, TableTh, TableThead, TableTr } from '@widgets/table';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import { ListLoader } from '@shared/uikit/loader';
import EmptyList from '@widgets/empty-list';
import { useCallback, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { SortType } from '@entities/util/ISort';
import {
  useDownloadManagersSalary,
  useGetManagersSalary,
  getManagersSalary,
} from '@entities/managers-perfomance';
import { toast } from 'react-toastify';
import { CalendarDays } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { MONTHS_FULL, MONTHS_FULL_FOR_SELECT } from '@app/contstants/months';
import { formatBtc } from '@app/utils/math';
import { clsx } from 'clsx';

type Props = {
  managerId: number | null;
  month?: number;
  year?: number;
  onPeriodChange?: (month: number, year: number) => void;
  onPeriodClear?: () => void;
};

const now = new Date();
const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const defaultMonth = prevMonthDate.getMonth() + 1;
const defaultYear = prevMonthDate.getFullYear();
const LAST_N_MONTHS = 12;

export const SalaryTable = ({
  managerId,
  month,
  year,
  onPeriodChange,
  onPeriodClear,
}: Props) => {
  const hasPeriod = month != null && year != null;
  const resolvedMonth = month ?? defaultMonth;
  const resolvedYear = year ?? defaultYear;
  const currentYear = resolvedYear;
  const downloadFetcher = useDownloadManagersSalary();
  const [sorting, setSorting] = useState<{ column: string; type: SortType } | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const order_by =
    sorting?.column === 'tier_percent'
      ? 'tier_percent'
      : sorting?.column === 'payment_btc'
        ? 'to_pay'
        : (sorting?.column ?? 'hashrate');
  const order_direction = sorting?.type === SortType.ASC ? 'asc' : 'desc';

  const singleQueryParams = useMemo(
    () =>
      managerId != null && hasPeriod
        ? {
            managerId,
            month: month!,
            year: currentYear,
            order_by,
            order_direction,
            limit: 100,
            page: 1,
          }
        : null,
    [managerId, hasPeriod, month, currentYear, order_by, order_direction]
  );

  const { data: singleList, isLoading: singleLoading } = useGetManagersSalary(
    singleQueryParams ?? undefined
  );

  const getMonths = useMemo(() => {
    const d = new Date();
    return Array.from({ length: LAST_N_MONTHS }, (_, i) => {
      const t = new Date(d.getFullYear(), d.getMonth() - i, 1);
      return { month: t.getMonth() + 1, year: t.getFullYear() };
    });
  }, []);

  const multiQueries = useQueries({
    queries:
      managerId != null && !hasPeriod
        ? getMonths.map(({ month: m, year: y }) => ({
            queryKey: ['managers-salary', managerId, m, y, order_by, order_direction],
            queryFn: () =>
              getManagersSalary({
                managerId,
                month: m,
                year: y,
                order_by,
                order_direction,
                limit: 100,
                page: 1,
              }),
          }))
        : [],
  });

  const mergedList = useMemo(() => {
    if (hasPeriod && singleList) return singleList;
    if (!hasPeriod && multiQueries.length > 0) {
      const allItems = multiQueries.flatMap(q =>
        q.isSuccess && q.data?.items ? q.data.items : []
      );
      if (!allItems.length) return null;
      const sortKey =
        order_by === 'tier_percent' ? 'tier_percent' : order_by === 'to_pay' ? 'to_pay' : 'hashrate';
      const sorted = [...allItems].sort((a, b) => {
        const av = Number(a[sortKey as keyof typeof a] ?? 0);
        const bv = Number(b[sortKey as keyof typeof b] ?? 0);
        return order_direction === 'asc' ? av - bv : bv - av;
      });
      return { items: sorted, total_items: sorted.length, total_pages: 1, current_page: 1 };
    }
    return null;
  }, [hasPeriod, singleList, multiQueries, order_by, order_direction]);

  const list = mergedList;
  const isLoading = hasPeriod ? singleLoading : multiQueries.some(q => q.isLoading);

  const handleSort = (column: string) => {
    setSorting(prev => {
      if (prev?.column === column) {
        return { column, type: prev.type === SortType.ASC ? SortType.DESC : SortType.ASC };
      }
      return { column, type: SortType.ASC };
    });
  };

  const totalSum = useMemo(() => {
    const items = list?.items;
    if (!items?.length) {
      return { payment_btc: 0, flex_contribution: 0 };
    }

    const paymentBtc = items.reduce((sum, item) => sum + Number(item.to_pay ?? 0), 0);
    const flexVal = items.reduce((s, it) => s + Number(it.flex_contribution ?? 0), 0);

    return { payment_btc: paymentBtc, flex_contribution: flexVal };
  }, [list]);

  const selectedMonthLabel =
    month != null && year != null
      ? `${MONTHS_FULL[resolvedMonth - 1]} ${resolvedYear}`
      : 'Выберите период';

  const handleMonthSelect = useCallback(
    (monthValue: string) => {
      onPeriodChange?.(Number(monthValue), resolvedYear);
      setIsOpen(false);
    },
    [onPeriodChange, resolvedYear]
  );

  const onDownload = async () => {
    if (!managerId) {
      toast.error('Выберите менеджера');
      return;
    }
    if (!hasPeriod) {
      toast.error('Выберите период для выгрузки');
      return;
    }
    try {
      await downloadFetcher.fetch({
        managerId,
        month: month!,
        year: currentYear,
        order_by,
        order_direction,
        limit: 100,
        page: 1,
        export_xlsx: true,
      });
      toast.success('Выгрузка началась');
    } catch {
      toast.error('Ошибка при выгрузке');
    }
  };

  return (
    <>
      {!isLoading ? (
        <>
          <div className="flex items-center justify-between w-full">
            <span className="font-semibold text-[20px]"></span>
            <div
              className="flex gap-[10px] h-[50px] self-stretch border-t border-x border-[#95959540]
                rounded-t-[16px] z-10 relative bg-[#141316]"
            >
              <div className="absolute w-full h-[20px] bg-[#141316] -bottom-[2px] left-0"></div>
              <div className="flex px-[30px] justify-between items-center gap-6">
                <div className="text-[12px] text-[#777777]">Всего</div>
                <div className="flex justify-end items-end gap-6">
                  <div className="flex items-end gap-[5px]">
                    <span className="text-[18px] ml-[10px]">
                      {list?.items?.length ? formatBtc(totalSum.payment_btc) : '—'}
                    </span>
                    <span className="text-[12px] text-[#777777] mb-[3px]">BTC</span>
                  </div>
                  <div className="flex items-end gap-[5px]">
                    <span className="text-[12px] text-[#777777]">Флекс:</span>
                    <span className="text-[18px]">
                      {list?.items?.length ? formatBtc(totalSum.flex_contribution) : '—'}
                    </span>
                    <span className="text-[12px] text-[#777777] mb-[3px]">BTC</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="py-[30px] px-[24px] mb-[20px] border border-opacity-30 solid rounded-[16px] border-[#959595]
              h-[100%] w-[100%] rounded-tr-[0px] relative"
          >
            {managerId != null && (
              <div className="flex items-center justify-between w-full mb-[34px] white">
                <div className="flex gap-x-[10px] text-[18px]">
                  <button type="button" className="bg-[#3455C3] py-2 px-[18px] rounded-md">
                    Выплаты по ViaBTC
                  </button>
                </div>
                <div className="flex items-center gap-x-[14px]">
                  <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
                    <Popover.Trigger asChild>
                      <div className="w-[168px] flex items-center bg-[#211F24] rounded-lg p-[10px] cursor-pointer">
                        <Typography
                          size={TextSize.size14}
                          className={month == null ? 'whitespace-nowrap' : ''}
                          text={selectedMonthLabel}
                        />
                        <CalendarDays size={16} className="ml-auto shrink-0" />
                      </div>
                    </Popover.Trigger>
                    <Popover.Portal>
                      <Popover.Content
                        className="bg-[#202020] text-white rounded-md overflow-hidden z-50 py-2 outline-none border border-[#2a2a2a]"
                        align="end"
                        sideOffset={8}
                        alignOffset={0}
                        style={{ width: 'var(--radix-popover-trigger-width)' }}
                      >
                        <div className="max-h-[300px] overflow-y-auto">
                          {MONTHS_FULL_FOR_SELECT.map(monthOption => {
                            const isSelected = Number(monthOption.value) === resolvedMonth;
                            return (
                              <div
                                key={monthOption.value}
                                className={clsx(
                                  'px-4 py-2 cursor-pointer hover:bg-[#2a2a2a] transition-colors',
                                  isSelected && 'bg-[#2a2a2a] text-white'
                                )}
                                onClick={() => handleMonthSelect(monthOption.value)}
                              >
                                <Typography
                                  size={TextSize.size14}
                                  className={
                                    isSelected ? 'text-white font-medium' : 'text-gray-400'
                                  }
                                  text={monthOption.label}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>
                  <DownloadButton onClick={onDownload} />
                </div>
              </div>
            )}
            {list?.items?.length ? (
              <>
                <div className="overflow-y-auto max-h-[310px]">
                  <Table>
                    <TableThead>
                      <TableTr>
                        <TableTh
                          title="Клиент"
                          className="w-[30%]"
                          sortName="client"
                          sort={sorting ?? undefined}
                          onSort={handleSort}
                        />
                        <TableTh
                          title="Tier"
                          className="w-[10%]"
                          sortName="tier"
                          sort={sorting ?? undefined}
                          onSort={handleSort}
                        />
                        <TableTh
                          title="% Tier"
                          className="w-[10%]"
                          sortName="tier_percent"
                          sort={sorting ?? undefined}
                          onSort={handleSort}
                        />
                        <TableTh
                          title="Выплата по клиенту, BTC"
                          className="w-[20%]"
                          sortName="payment_btc"
                          sort={sorting ?? undefined}
                          onSort={handleSort}
                        />
                        <TableTh
                          title="Флекс (от %), BTC"
                          className="w-[15%]"
                        />
                      </TableTr>
                    </TableThead>
                    <TableBody>
                      {list.items.map((item, index) => (
                        <TableTr key={`${item.client_id ?? 'client'}-${index}`}>
                          <TableTd
                            title={
                              <div className="flex items-center gap-2 cursor-pointer">
                                <ValueWithSign value={item.client} color="bg-gradient-blue" />
                              </div>
                            }
                          />
                          <TableTd title={`${item.tier}`} />
                          <TableTd title={`${Math.round(Number(item.tier_percent) * 100)}%`} />
                          <TableTd title={formatBtc(item.to_pay)} />
                          <TableTd
                            title={
                              item.flex_contribution != null ? formatBtc(item.flex_contribution) : '—'
                            }
                          />
                        </TableTr>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <EmptyList />
            )}
          </div>
        </>
      ) : (
        <ListLoader loading />
      )}
    </>
  );
};
